import { useState, useEffect, useCallback } from "react";
import { isEmbroideryTrace } from "../../config/appVariant";
import { IonSpinner } from "@ionic/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/AuthContext";
import { createPortalLinkFunc } from "../../lib/firebase/firebase";
import { getSubscriptionDetails } from "../../lib/stripe/getSubscriptionDetails";
import { isNativeiOS } from "../../lib/utils/platform";
import { Capacitor } from "@capacitor/core";
import IOSPaywall from "../PaywallModal/IOSPaywall";
import CancelFlowModal from "../CancelFlowModal/CancelFlowModal";
import branding from "../../config/branding";
import "./SubscriptionPage.css";

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatPrice = (priceData) => {
  if (!priceData) return null;
  const amount = priceData.unit_amount;
  const currency = (priceData.currency || "usd").toUpperCase();
  if (amount == null) return null;
  const formatted = (amount / 100).toFixed(2);
  return `$${formatted}`;
};

const formatInterval = (priceData) => {
  if (!priceData?.recurring?.interval) return "";
  const interval = priceData.recurring.interval;
  if (interval === "month") return "/month";
  if (interval === "year") return "/year";
  return `/${interval}`;
};

const StatusBadge = ({ status, cancelAtPeriodEnd }) => {
  let label = status;
  let className = "sub-page__badge";

  if (cancelAtPeriodEnd && (status === "active" || status === "trialing")) {
    label = "Canceling";
    className += " sub-page__badge--warning";
  } else if (status === "active") {
    label = "Active";
    className += " sub-page__badge--active";
  } else if (status === "trialing") {
    label = "Free Trial";
    className += " sub-page__badge--trial";
  } else if (status === "canceled") {
    label = "Canceled";
    className += " sub-page__badge--canceled";
  } else {
    label = status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown";
    className += " sub-page__badge--neutral";
  }

  return <span className={className}>{label}</span>;
};

// --- State views ---

const LifetimeProView = () => (
  <div className="sub-page__plan-card">
    <div className="sub-page__plan-header">
      <h3 className="sub-page__plan-name">Lifetime Pro</h3>
      <span className="sub-page__badge sub-page__badge--lifetime">Lifetime</span>
    </div>
    <div className="sub-page__detail-rows">
      <div className="sub-page__detail-row">
        <span className="sub-page__detail-label">Status</span>
        <span className="sub-page__detail-value">Active — never expires</span>
      </div>
      <div className="sub-page__detail-row">
        <span className="sub-page__detail-label">Billing</span>
        <span className="sub-page__detail-value">One-time purchase</span>
      </div>
    </div>
    <div className="sub-page__info-box sub-page__info-box--success">
      You have lifetime access to all {branding.appName} features. No further payments needed.
    </div>
  </div>
);

const RevenueCatView = ({ productId, onCancelClick }) => {
  const planName = productId?.toLowerCase().includes("yearly")
    ? "Yearly"
    : productId?.toLowerCase().includes("monthly")
      ? "Monthly"
      : "Pro";

  const handleManageInAppStore = () => {
    if (Capacitor.isNativePlatform()) {
      window.open("https://apps.apple.com/account/subscriptions", "_system");
    } else {
      window.open("https://apps.apple.com/account/subscriptions", "_blank");
    }
  };

  return (
    <div className="sub-page__plan-card">
      <div className="sub-page__plan-header">
        <h3 className="sub-page__plan-name">{branding.appName} — {planName}</h3>
        <span className="sub-page__badge sub-page__badge--apple">Managed by Apple</span>
      </div>
      <div className="sub-page__detail-rows">
        <div className="sub-page__detail-row">
          <span className="sub-page__detail-label">Status</span>
          <span className="sub-page__detail-value">Active</span>
        </div>
        <div className="sub-page__detail-row">
          <span className="sub-page__detail-label">Billing</span>
          <span className="sub-page__detail-value">Through the App Store</span>
        </div>
      </div>
      <div className="sub-page__info-box">
        Your subscription is managed through the App Store. To change your plan, update payment, or cancel, use your Apple ID settings.
      </div>
      <button className="sub-page__btn sub-page__btn--primary" onClick={handleManageInAppStore}>
        Manage in App Store
      </button>

      <button
        className="sub-page__btn"
        onClick={onCancelClick}
        style={{
          background: "none",
          border: "1.5px solid #fecaca",
          color: "#dc2626",
          fontSize: 13,
          padding: "10px 16px",
        }}
      >
        Cancel Subscription
      </button>

      <div className="sub-page__platform-notice">
        <span className="sub-page__platform-notice-icon">ℹ️</span>
        <p>
          This subscription was purchased through the App Store. To manage, change, or cancel it, use your <strong>Apple ID settings</strong>. This cannot be done from the web app.
        </p>
      </div>
    </div>
  );
};

const StripeSubscriptionView = ({ subscription, onManageBilling, portalLoading, onCancelClick }) => {
  const { price, product, status, cancelAtPeriodEnd, currentPeriodEnd, trialEnd } = subscription;
  const priceStr = formatPrice(price);
  const interval = formatInterval(price);
  const planName = product?.name || (interval === "/year" ? "Yearly" : "Monthly");
  const isTrialing = status === "trialing";
  const isCanceling = cancelAtPeriodEnd;

  return (
    <div className="sub-page__plan-card">
      <div className="sub-page__plan-header">
        <h3 className="sub-page__plan-name">{branding.appName} — {planName}</h3>
        <StatusBadge status={status} cancelAtPeriodEnd={cancelAtPeriodEnd} />
      </div>

      <div className="sub-page__detail-rows">
        {priceStr && (
          <div className="sub-page__detail-row">
            <span className="sub-page__detail-label">Price</span>
            <span className="sub-page__detail-value sub-page__detail-value--bold">
              {priceStr}<span className="sub-page__detail-muted">{interval}</span>
            </span>
          </div>
        )}
        {isTrialing && trialEnd && (
          <div className="sub-page__detail-row">
            <span className="sub-page__detail-label">Trial ends</span>
            <span className="sub-page__detail-value">{formatDate(trialEnd)}</span>
          </div>
        )}
        {currentPeriodEnd && (
          <div className="sub-page__detail-row">
            <span className="sub-page__detail-label">{isCanceling ? "Access until" : "Renews on"}</span>
            <span className="sub-page__detail-value">{formatDate(currentPeriodEnd)}</span>
          </div>
        )}
      </div>

      {isCanceling && (
        <div className="sub-page__info-box sub-page__info-box--warning">
          Your subscription will end on {formatDate(currentPeriodEnd)}. You'll keep access until then. Resubscribe anytime from Manage Billing.
        </div>
      )}

      <button
        className="sub-page__btn sub-page__btn--primary"
        onClick={onManageBilling}
        disabled={portalLoading}
      >
        {portalLoading ? <IonSpinner name="lines" style={{ width: 20, height: 20 }} /> : "Manage Billing"}
      </button>
      <p className="sub-page__btn-hint">Update payment method or view invoices</p>

      {!isCanceling && (
        <button
          className="sub-page__btn sub-page__btn--cancel"
          onClick={onCancelClick}
          style={{
            background: "none",
            border: "1.5px solid #fecaca",
            color: "#dc2626",
            fontSize: 13,
            padding: "10px 16px",
          }}
        >
          Cancel Subscription
        </button>
      )}

      <div className="sub-page__platform-notice">
        <span className="sub-page__platform-notice-icon">ℹ️</span>
        <p>
          This subscription was purchased on the web. To manage, change, or cancel it, use the buttons above. This cannot be done from the iOS app.
        </p>
      </div>
    </div>
  );
};

const FreePlanView = ({ onUpgrade }) => (
  <div className="sub-page__plan-card">
    <div className="sub-page__plan-header">
      <h3 className="sub-page__plan-name">Free Plan</h3>
      <span className="sub-page__badge sub-page__badge--neutral">Free</span>
    </div>
    <div className="sub-page__free-limits">
      <p className="sub-page__free-limits-title">Your current plan includes:</p>
      <ul className="sub-page__free-list">
        <li>Basic image-to-SVG conversion</li>
        <li>Preview only — no exports</li>
      </ul>
      <p className="sub-page__free-limits-title" style={{ marginTop: 12 }}>Upgrade to Pro for:</p>
      <ul className="sub-page__pro-list">
        <li>Unlimited SVG exports, no watermark</li>
        <li>Full Access to Embroidery Editor</li>
        <li>Commercial License</li>
        <li>AI Retrace, Modify & Remove Background</li>
        <li>Bulk Convert & Resize</li>
        {!isEmbroideryTrace && <li>AI Image Generator</li>}
      </ul>
    </div>
    <button className="sub-page__btn sub-page__btn--upgrade" onClick={onUpgrade}>
      Upgrade to Pro
    </button>
  </div>
);

// --- Main component ---

const SubscriptionPage = ({ onBack }) => {
  const { user, userIsPremium } = useAuth();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [showIOSPaywall, setShowIOSPaywall] = useState(false);
  const [showCancelFlow, setShowCancelFlow] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getSubscriptionDetails(user.uid);
      setDetails(data);
    } catch (err) {
      console.error("Failed to load subscription details:", err);
      setError("Couldn't load subscription details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await createPortalLinkFunc({
        returnUrl: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (err) {
      console.error("Error opening customer portal:", err);
      setPortalLoading(false);
    }
  };

  const handleUpgrade = () => {
    if (isNativeiOS()) {
      setShowIOSPaywall(true);
    } else {
      window.location.href = "/checkout";
    }
  };

  // Determine which view to show
  const isPro = !!userIsPremium;
  const isLifetime = userIsPremium === "lifetime-pro";
  // isRevenueCat: either Firestore confirms it, or userIsPremium was set
  // directly from RC entitlements before the webhook propagated to Firestore.
  const isRevenueCat =
    details?.subscriptionSource === "revenuecat" ||
    userIsPremium === "revenuecat";
  const hasStripeSub = details?.subscription != null;
  const isFree = !userIsPremium;

  return (
    <div className="sub-page">
      <div className="sub-page__container">
        <div className="sub-page__top-bar">
          <button className="sub-page__back-btn" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
        </div>

        <h2 className="sub-page__title">My Subscription</h2>

        {loading ? (
          <div className="sub-page__loading">
            <IonSpinner name="crescent" style={{ width: 36, height: 36, color: "#0d9488" }} />
            <p>Loading your plan...</p>
          </div>
        ) : error ? (
          <div className="sub-page__error">
            <p>{error}</p>
            <button className="sub-page__btn sub-page__btn--primary" onClick={fetchDetails}>
              Retry
            </button>
          </div>
        ) : isLifetime ? (
          <LifetimeProView />
        ) : isRevenueCat ? (
          <RevenueCatView productId={details.revenuecatProductId} onCancelClick={() => setShowCancelFlow(true)} />
        ) : hasStripeSub ? (
          <StripeSubscriptionView
            subscription={details.subscription}
            onManageBilling={handleManageBilling}
            portalLoading={portalLoading}
            onCancelClick={() => setShowCancelFlow(true)}
          />
        ) : isFree ? (
          <FreePlanView onUpgrade={handleUpgrade} />
        ) : (
          <div className="sub-page__plan-card">
            <div className="sub-page__plan-header">
              <h3 className="sub-page__plan-name">{branding.appName}</h3>
              <StatusBadge status="active" cancelAtPeriodEnd={false} />
            </div>
            <div className="sub-page__info-box">
              Your subscription is active. Use Manage Billing to view details.
            </div>
            <button
              className="sub-page__btn sub-page__btn--primary"
              onClick={handleManageBilling}
              disabled={portalLoading}
            >
              {portalLoading ? <IonSpinner name="lines" style={{ width: 20, height: 20 }} /> : "Manage Billing"}
            </button>
          </div>
        )}

        <div className="sub-page__footer">
          {isPro && !isLifetime && (
            <p className="sub-page__footer-platform">
              {isRevenueCat
                ? "Subscribed via the App Store. Manage your subscription in Apple ID settings."
                : "Subscribed via the web. Manage your subscription from the Stripe billing portal."}
            </p>
          )}
          <p>Need help? Contact support@svgtrace.com</p>
        </div>
      </div>

      {showIOSPaywall && (
        <IOSPaywall
          standalone
          onClose={() => setShowIOSPaywall(false)}
          onSuccess={() => {
            setShowIOSPaywall(false);
            fetchDetails();
          }}
        />
      )}

      <CancelFlowModal
        isOpen={showCancelFlow}
        onClose={() => {
          setShowCancelFlow(false);
          fetchDetails();
        }}
        onCanceled={() => fetchDetails()}
        isIOSSub={isRevenueCat}
      />
    </div>
  );
};

export default SubscriptionPage;
